(ns serv-ui.prompt-view
  "One operator prompt, drawn: its Markdown as a page and its directives
  (`serv.prompt-markup`) as controls. The answer is held here until Submit
  sends it; an answered prompt shows what was answered, read-only."
  (:require-macros [kc.core :refer [def-ui $]])
  (:require ["highlight.js/lib/languages/clojure" :as hl-clojure]
            ["lowlight" :refer [common createLowlight]]
            ["react" :as react]
            [clojure.string :as str]
            [kc.core :as kc]
            [serv-ui.api :as api]
            [serv-ui.mui :as mui :refer [sx]]
            [serv-ui.theme :as theme]
            [serv.prompt-markup :as markup]))

(defn- files-path [id rel]
  (str "/api/operator-prompts/" (js/encodeURIComponent id) "/files/"
       (->> (str/split rel #"/") (remove str/blank?) (map js/encodeURIComponent) (str/join "/"))))

(defn- attachment-rel
  "The attachment a Markdown address points at, or nil when it points out."
  [attachments url]
  (let [u (str/replace (str url) #"^\./" "")
        u (str/replace u #"^attachments/" "")]
    (when (and (not (re-find #"^[a-z]+:" u)) (some #{u} attachments))
      (str "attachments/" u))))

(defn- save-blob! [id rel]
  (-> (api/blob> (files-path id rel))
      (.then (fn [blob]
               (let [url (js/URL.createObjectURL blob)
                     a   (js/document.createElement "a")]
                 (set! (.-href a) url)
                 (set! (.-download a) (last (str/split rel #"/")))
                 (.click a)
                 (js/setTimeout #(js/URL.revokeObjectURL url) 1000))))
      (.catch #(js/alert (str "Download failed: " (ex-message %))))))

(def-ui Attachment-image [id rel alt]
  (let [[src set-src] (kc/use-state nil)]
    (kc/use-effect
     [id rel]
     (let [live? (volatile! true)
           !url  (volatile! nil)]
       (-> (api/blob> (files-path id rel))
           (.then (fn [b] (when @live?
                            (vreset! !url (js/URL.createObjectURL b))
                            (set-src @!url))))
           (.catch (fn [_] nil)))
       (fn [] (vreset! live? false) (some-> @!url js/URL.revokeObjectURL))))
    (if src
      ($ :img {:src src :alt alt :style {:maxWidth "100%"}})
      ($ :span {:style {:opacity 0.6}} (str "[" alt "]")))))

;;; ------------------------------------------------------------ controls

(defn- toggle-in [vs v]
  (if (some #{v} vs) (vec (remove #{v} vs)) (conj (vec vs) v)))

(def toggle-sx
  "Options and toggles: a frame in the control colour, filled once chosen."
  (sx {:textTransform "none" :m "4px" :py "8px" :px "16px" :minHeight 44 :gap 1
       :fontSize "1rem" :verticalAlign "baseline"
       :color "control.main" :borderColor "control.main"
       "&.Mui-selected, &.Mui-selected:hover" {:bgcolor "control.main" :color "control.contrastText"}
       "&.Mui-selected.Mui-disabled" {:bgcolor "control.main" :color "control.contrastText" :opacity 0.85}
       "&.Mui-disabled" {:borderColor "control.main" :opacity 0.5}}))

(def-ui Option [ctl ctx]
  (let [{:keys [answer set-value fields read-only?]} ctx
        k        (keyword (:group ctl))
        f        (get fields k)
        v        (get answer k)
        one?     (= 1 (:max f))
        vs       (cond (nil? v) [] (sequential? v) v :else [v])
        on?      (boolean (some #{(:value ctl)} vs))
        full?    (and (not one?) (>= (count vs) (:max f)))]
    ($ mui/ToggleButton
       {:value    (:value ctl)
        :selected on?
        :data-hk  (when-not read-only? "click")
        :color    "control"
        :disabled (or read-only? (and full? (not on?)))
        :sx       (if (:flush? ctx) #js [toggle-sx (sx {:ml 0})] toggle-sx)
        :onChange (fn [_]
                    (set-value k (if one?
                                   (if (and on? (zero? (:min f))) nil (:value ctl))
                                   (toggle-in vs (:value ctl)))))}
       (or (:label ctl) (:value ctl)))))

(def-ui CheckIcon
  "A toggle's box: empty while off, ticked while on."
  [on?]
  ($ :svg {:viewBox "0 0 24 24" :width 20 :height 20 :fill "currentColor" :aria-hidden true
           :style {:flexShrink 0 :marginLeft -4}}
     ($ :path {:d (if on?
                    "M19 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2zm-9 14-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
                    "M19 5v14H5V5h14m0-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2z")})))

(def-ui Toggle [ctl ctx]
  (let [{:keys [answer set-value read-only?]} ctx
        k (keyword (:id ctl))]
    ($ mui/ToggleButton
       {:value    "on"
        :selected (true? (get answer k))
        :data-hk  (when-not read-only? "click")
        :color    "control"
        :disabled read-only?
        :sx       (if (:flush? ctx) #js [toggle-sx (sx {:ml 0})] toggle-sx)
        :onChange (fn [_] (set-value k (not (true? (get answer k)))))}
       ($ CheckIcon {:on? (true? (get answer k))})
       (or (:label ctl) (:id ctl)))))

(def-ui Field [ctl ctx]
  (let [{:keys [answer set-value read-only?]} ctx
        k (keyword (:id ctl))]
    ($ mui/TextField
       {:value       (or (get answer k) "")
        :label       (or (:label ctl) (:id ctl))
        :placeholder (:placeholder ctl)
        :required    (:required ctl)
        :fullWidth   true
        :size        "small"
        :multiline   (= "text" (:kind ctl))
        :minRows     (when (= "text" (:kind ctl)) (or (:rows ctl) 3))
        :disabled    read-only?
        :data-hk     (when-not read-only? "field")
        :color       "control"
        :sx          (sx {:my 1})
        :onChange    (fn [^js e] (set-value k (-> e .-target .-value)))})))

(def-ui Upload [ctl ctx]
  (let [{:keys [files set-files read-only? answer prompt-id]} ctx
        k      (keyword (:id ctl))
        chosen (get files k [])]
    ($ mui/Box {:sx (sx {:my 1 :display "flex" :flexWrap "wrap" :gap 1 :alignItems "center"})}
       ($ mui/Typography {:sx (sx {:mr 1})}
          (str (or (:label ctl) (:id ctl)) (when (:required ctl) " *")))
       (if read-only?
         (if (seq (get answer k))
           (for [rel (get answer k)]
             ($ mui/Chip {:key rel :label (last (str/split rel #"/")) :variant "outlined"
                          :onClick (fn [_] (save-blob! prompt-id rel))}))
           ($ mui/Typography {:sx (sx {:color "text.secondary"})} "no file"))
         ($ :<>
            ($ mui/Button {:variant "outlined" :color "control" :component "label" :data-hk "click" :sx (sx {:minHeight 44})}
               (if (:multiple ctl) "Choose files" "Choose a file")
               ($ :input {:type "file" :hidden true :accept (:accept ctl) :multiple (:multiple ctl)
                          :onChange (fn [^js e]
                                      (let [fs (vec (array-seq (-> e .-target .-files)))]
                                        (set-files k (if (:multiple ctl) (into chosen fs) (vec (take 1 fs))))
                                        (set! (-> e .-target .-value) "")))}))
            (for [[i ^js f] (map-indexed vector chosen)]
              ($ mui/Chip {:key (str i (.-name f)) :label (.-name f) :size "small"
                           :onDelete (fn [_] (set-files k (vec (concat (take i chosen) (drop (inc i) chosen)))))})))))))

(defn- control-el [^js node ctx key]
  (let [ctl (markup/control node)
        ;; a control on its own line starts flush with the text
        ctx (cond-> ctx (= "leafDirective" (.-type node)) (assoc :flush? true))]
    (case (:kind ctl)
      "option"           ($ Option {:key key :ctl ctl :ctx ctx})
      "toggle"           ($ Toggle {:key key :ctl ctl :ctx ctx})
      ("input" "text")   ($ Field {:key key :ctl ctl :ctx ctx})
      "upload"           ($ Upload {:key key :ctl ctl :ctx ctx}))))

;;; ------------------------------------------------------------ markdown

(def ^:private n theme/nord)

(def mono "'JetBrainsMono Nerd Font', ui-monospace, monospace")

(def heading-colors
  "One Nord frost per level, down to h3; below that, h3's."
  {1 (:nord8 n) 2 (:nord9 n) 3 (:nord7 n)})

;;; Code blocks: lowlight (highlight.js' grammars, as a syntax tree, so no
;;; HTML is set by hand), its common languages plus Clojure and EDN.

(defonce ^:private lowlight
  (doto (createLowlight common)
    (.register #js {:clojure hl-clojure})))

(def code-sx
  "Nord's colours for highlight.js' classes."
  (sx {:fontFamily mono :fontSize "0.875rem" :lineHeight 1.6 :color (:nord4 n)
       "& .hljs-comment, & .hljs-quote"                       {:color "#616e88" :fontStyle "italic"}
       "& .hljs-keyword, & .hljs-literal, & .hljs-tag, & .hljs-selector-tag, & .hljs-meta .hljs-keyword" {:color (:nord9 n)}
       "& .hljs-string, & .hljs-meta .hljs-string, & .hljs-addition" {:color (:nord14 n)}
       "& .hljs-number"                                       {:color (:nord15 n)}
       "& .hljs-title, & .hljs-section, & .hljs-built_in, & .hljs-name" {:color (:nord8 n)}
       "& .hljs-type, & .hljs-class, & .hljs-attr, & .hljs-attribute, & .hljs-property" {:color (:nord7 n)}
       "& .hljs-symbol, & .hljs-regexp, & .hljs-template-variable" {:color (:nord13 n)}
       "& .hljs-variable, & .hljs-params, & .hljs-subst"      {:color (:nord4 n)}
       "& .hljs-meta"                                         {:color (:nord10 n)}
       "& .hljs-deletion"                                     {:color (:nord11 n)}
       "& .hljs-bullet, & .hljs-link"                         {:color (:nord12 n)}
       "& .hljs-emphasis"                                     {:fontStyle "italic"}
       "& .hljs-strong, & .hljs-section"                      {:fontWeight 600}}))

(defn- hast-el
  "A lowlight tree as React elements: spans with classes, and text."
  [^js node key]
  (case (.-type node)
    "root"    ($ :<> {:key key} (map-indexed (fn [i c] (hast-el c i)) (.-children node)))
    "element" ($ :span {:key key :className (str/join " " (or (some-> node .-properties .-className) #js []))}
                 (map-indexed (fn [i c] (hast-el c i)) (.-children node)))
    "text"    (.-value node)
    nil))

(defn- highlighted
  "`code` in `lang`, highlighted; as it is when the language is unknown."
  [code lang]
  (let [lang (some-> lang str/lower-case)]
    (if (and lang (.registered lowlight lang))
      (hast-el (.highlight lowlight lang code) 0)
      code)))

(def-ui Code-block [code lang]
  (let [out (react/useMemo #(highlighted code lang) #js [code lang])]
    ($ mui/Box {:component "pre"
                :sx (sx {:my 2 :p 1.5 :overflowX "auto" :bgcolor (:nord1 n) :borderRadius 1
                         :borderLeft 3 :borderColor (:nord3 n)})}
       ($ mui/Box {:component "code" :sx code-sx} out))))

(declare node-el)

(defn- kids [^js node ctx]
  (map-indexed (fn [i c] (node-el c ctx i)) (or (.-children node) #js [])))

(defn node-el [^js node ctx key]
  (let [t (.-type node)]
    (case t
      "root"          ($ :<> {:key key} (kids node ctx))
      "paragraph"     ($ mui/Typography {:key key :component "p" :sx (sx {:my 1 :lineHeight 2})} (kids node ctx))
      "heading"       ($ mui/Typography {:key key :variant "h6" :component (str "h" (min 6 (.-depth node)))
                                         :sx (sx {:mt 3 :mb 1 :fontWeight 600
                                                  :color (get heading-colors (.-depth node) (heading-colors 3))
                                                  :fontSize (case (.-depth node) 1 "1.25rem" 2 "1.125rem" "1rem")})}
                         (kids node ctx))
      "text"          (.-value node)
      "emphasis"      ($ :em {:key key} (kids node ctx))
      "strong"        ($ :strong {:key key} (kids node ctx))
      "delete"        ($ :del {:key key} (kids node ctx))
      "inlineCode"    ($ mui/Box {:key key :component "code"
                                  :sx (sx {:fontFamily mono :fontSize "0.9em" :color (:nord13 n)
                                           :bgcolor (:nord1 n) :px 0.5 :py 0.25 :borderRadius 0.5})}
                         (.-value node))
      "code"          ($ Code-block {:key key :code (.-value node) :lang (.-lang node)})
      "break"         ($ :br {:key key})
      "thematicBreak" ($ mui/Divider {:key key :sx (sx {:my 2})})
      "blockquote"    ($ mui/Box {:key key :component "blockquote"
                                  :sx (sx {:mx 0 :pl 2 :borderLeft 3 :borderColor "divider" :color "text.secondary"})}
                         (kids node ctx))
      "list"          (if (.-ordered node)
                        ($ :ol {:key key :start (.-start node)} (kids node ctx))
                        ($ :ul {:key key} (kids node ctx)))
      "listItem"      ($ :li {:key key}
                         (when (boolean? (.-checked node))
                           ($ :input {:type "checkbox" :checked (.-checked node) :readOnly true :disabled true}))
                         ;; A tight list's items hold paragraphs; drawn inline.
                         (map-indexed (fn [i ^js c]
                                        (if (and (.-spread node) (= "paragraph" (.-type c)))
                                          (node-el c ctx i)
                                          (if (= "paragraph" (.-type c))
                                            ($ :span {:key i} (kids c ctx))
                                            (node-el c ctx i))))
                                      (.-children node)))
      "link"          (let [rel (attachment-rel (:attachments ctx) (.-url node))]
                        ($ mui/Link {:key key
                                     :href (if rel "#" (.-url node))
                                     :target (when-not rel "_blank") :rel "noreferrer"
                                     :onClick (when rel (fn [^js e] (.preventDefault e) (save-blob! (:prompt-id ctx) rel)))}
                           (kids node ctx)))
      "image"         (if-let [rel (attachment-rel (:attachments ctx) (.-url node))]
                        ($ Attachment-image {:key key :id (:prompt-id ctx) :rel rel :alt (.-alt node)})
                        ($ :img {:key key :src (.-url node) :alt (.-alt node) :style {:maxWidth "100%"}}))
      "table"         ($ mui/Box {:key key :sx (sx {:my 2 :maxWidth "100%" :overflowX "auto"})}
                       ($ mui/Table {:size "small" :sx (sx {:overflowWrap "normal"})}
                         ($ mui/TableBody
                            (map-indexed
                             (fn [i ^js row]
                               ($ mui/TableRow {:key i}
                                  (map-indexed (fn [j ^js cell]
                                                 ($ mui/TableCell {:key j :sx (sx {:fontWeight (if (zero? i) 600 400)})
                                                                   :align (or (aget (.-align node) j) js/undefined)}
                                                    (kids cell ctx)))
                                               (.-children row))))
                             (.-children node)))))
      "html"          ($ :code {:key key} (.-value node))
      ("leafDirective" "textDirective" "containerDirective")
      (cond (markup/control? node) (control-el node ctx key)
            (= "textDirective" t)  ($ :<> {:key key} (str ":" (.-name node)) (kids node ctx))
            :else                  nil)
      ;; definitions, footnotes and what else GFM has: their text, if any
      (if (.-children node)
        ($ :<> {:key key} (kids node ctx))
        (when (string? (.-value node)) (.-value node))))))

(def-ui Markdown [tree ctx]
  (node-el tree ctx 0))
